import './HexagonButton.scss';

import * as React from 'react';

import cx from 'classnames';
import nanoid from 'nanoid';

export interface HexagonButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Set this to `null` when providing custom color & background classes. */
  appearance?: 'default';
  /** This should be a `text-` Tailwind utility class. */
  colorClass?: string;
  /** This should be a `fill-` Tailwind utility class. */
  shadowClass?: string;
  /** This should be a `fill-` Tailwind utility class. */
  backgroundClass?: string;
  /** Affects rotation speed as well as dimensions. */
  size?: 'default' | 'small';
  /**
   * Should be set to `true` if providing a custom background class. Also
   * affects shadow distance.
   */
  isOnWhite?: boolean;
  /**
   * Force a specific rotation offset. Useful for deterministic visual diff
   * checking. Will be random by default.
   */
  baseRotation?: number;
  isHovered?: boolean;
}

interface State {
  id: string;
  baseRotation: number;
  rotationDir: number;
}

/**
 * @deprecated No longer used.
 */
export class HexagonButton extends React.Component<HexagonButtonProps, State> {
  public static defaultProps = {
    type: 'button',
    appearance: 'default',
    size: 'default',
    isOnWhite: false,
    onClick: () => {},
  };

  constructor(props: HexagonButtonProps) {
    super(props);

    this.state = {
      id: nanoid(10),
      baseRotation: this.props.baseRotation ?? Math.round(Math.random() * 359),
      rotationDir: this.props.baseRotation ?? Math.random(),
    };
  }

  handleOnClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    this.props.onClick(event);
  };

  render() {
    const {
      children,
      appearance,
      colorClass,
      shadowClass,
      backgroundClass,
      size,
      isOnWhite,
      isHovered,
      ...props
    } = this.props;
    const hexSize = size === 'small' ? 108 : 168;
    const { id, baseRotation, rotationDir } = this.state;
    const buttonClass = cx(
      'relative p-0 m-0 bg-transparent border-0 rounded-2 cursor-pointer outline-none appearance-none',
      'hexagon-button',
      {
        'text-cyan-600 hover:text-cyan-700 focus:text-cyan-700':
          appearance === 'default',
        'is-small': size === 'small',
        'is-reversed': rotationDir > 0.5,
        'is-hovered': isHovered,
        'text-cyan-700': isHovered && appearance === 'default',
        [colorClass as string]: colorClass,
      }
    );
    const shadeClass = cx('hexagon__spin origin-center', {
      'fill-grey-300': appearance === 'default',
      'fill-current': appearance !== 'default',
      [shadowClass as string]: shadowClass,
    });
    const bodyClass = cx('hexagon__spin origin-center', {
      'fill-white': !isOnWhite,
      'fill-grey-50': isOnWhite && !backgroundClass,
      [backgroundClass as string]: backgroundClass && isOnWhite,
    });

    return (
      <button className={buttonClass} onClick={this.handleOnClick} {...props}>
        <div className="flex relative items-center justify-center">
          <svg
            className="overflow-visible"
            aria-hidden={true}
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            width={hexSize}
            height={hexSize}
            viewBox="0 0 168 168"
          >
            <defs>
              <path
                id={`hex-${id}`}
                strokeWidth="3"
                vectorEffect="non-scaling-stroke"
                fillRule="evenodd"
                d="M165.302 94.59l-31.92 55.324A20.152 20.152 0 01115.924 160h-63.84a20.16 20.16 0 01-17.459-10.086L2.705 94.59a20.174 20.174 0 010-20.18l31.92-55.324A20.152 20.152 0 0152.083 9h63.84a20.16 20.16 0 0117.459 10.086l31.92 55.324a20.22 20.22 0 010 20.18z"
              />
            </defs>
            <g transform={`translate(0 ${isOnWhite ? 4 : 18})`}>
              <g className="hexagon__shadow transition duration-300 origin-bottom ease-bounce">
                <g
                  className="origin-center"
                  style={{ transform: `rotate(${baseRotation}deg)` }}
                >
                  <use
                    className={shadeClass}
                    data-testid="hex-shadow"
                    opacity={appearance === 'default' || isOnWhite ? 1 : 0.33}
                    xlinkHref={`#hex-${id}`}
                  />
                </g>
              </g>
            </g>
            <g className="hexagon__body transition duration-300 origin-bottom ease-bounce">
              <g
                className="origin-center"
                style={{ transform: `rotate(${baseRotation}deg)` }}
              >
                <use
                  className={bodyClass}
                  data-testid="hex-body"
                  xlinkHref={`#hex-${id}`}
                />
                <g className="hexagon__outline">
                  <use
                    className="origin-center hexagon__spin"
                    fill="none"
                    xlinkHref={`#hex-${id}`}
                  />
                </g>
              </g>
            </g>
          </svg>
          <span className="hexagon__content absolute inset-0 flex items-center justify-center font-display font-800 text-lg text-inherit transition duration-300 origin-bottom ease-bounce">
            {children}
          </span>
        </div>
      </button>
    );
  }
}
